import React, { MouseEventHandler, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { applyStyleModifiers } from '../../lib/style-modifiers/style-modifiers';
import { robotoFonts } from '../typography';

const createThrottle = () => {
  let throttling = false;
  return (func, timeout) => {
    if (!throttling) {
      throttling = true;
      func();
      setTimeout(() => {
        throttling = false;
      }, timeout);
    }
  };
};

export interface IButton {
  autoFocus?: boolean;
  buttonSize?: string;
  text?: any;
  className?: string;
  disabled?: boolean;
  modifiers?: Modifier | Modifier[];
  name?: string;
  onClick?: MouseEventHandler | ((e: {}) => void);
  onKeyPress?: (event: React.KeyboardEvent) => void;
  theme?: any;
  type?: string;
  width?: any;
  children?: any;
  active?: boolean;
  throttleFor?: number;
  dataTestId?: string;
  asElement?: string;
}

export const throttleIntervalInMillisecondsDefault = 300;

export const Button = React.forwardRef((props: IButton, ref: any) => {
  const defaultProps: any = {
    buttonSize: 'standard',
    className: 'qa-button',
    disabled: false,
    modifiers: 'primary',
    type: 'button',
    width: 'auto',
    as: props.asElement ?? 'button'
  };
  const throttle = useMemo(createThrottle, []);
  const { throttleFor = throttleIntervalInMillisecondsDefault, onClick = () => {} } = props;
  const onClickFn = throttleFor ? event => throttle(() => onClick(event), throttleFor) : onClick;
  return (
    <ButtonComponent
      // TODO: these "defaultProps" get all overwritten by the props defined below (even if undefined)
      {...defaultProps}
      data-testid={props.dataTestId}
      autoFocus={props.autoFocus}
      buttonSize={props.buttonSize}
      className={props.className}
      disabled={props.disabled}
      modifiers={props.modifiers}
      name={props.name || props.className}
      onClick={onClickFn}
      type={props.type || 'button'}
      width={props.width}
      onKeyPress={props.onKeyPress}
      active={props.active}
      ref={ref}>
      {props.children || props.text}
    </ButtonComponent>
  );
});
Button.displayName = 'Button';

export default Button;

export type Modifier = keyof typeof MODIFIER_CONFIG;

// TODO: Tidy up modifier config???
const MODIFIER_CONFIG = {
  primary: ({ theme }) => css`
    color: ${theme.colors.buttons.primaryContrast};
    background-color: ${theme.colors.buttons.primary};
    border: 1px solid ${theme.colors.buttons.primary} !important;
  `,
  primaryRound: ({ theme }) => css`
    color: ${theme.colors.buttons.primaryContrast};
    background-color: ${theme.colors.buttons.primary};
    border: 1px solid ${theme.colors.buttons.primary};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  primaryRoundOutline: ({ theme }) => css`
    background-color: transparent;
    color: ${theme.colors.buttons.primary};
    border: 1px solid ${theme.colors.buttons.primary};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  secondary: ({ theme }) => css`
    background-color: transparent;
    border: 1px solid ${theme.components.buttons.secondary.borderColor};
  `,
  secondaryRound: ({ theme }) => css`
    background-color: transparent;
    border: 1px solid ${theme.components.buttons.secondary.borderColor};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  secondaryInverted: ({ theme }) => css`
    background-color: transparent;
    border: 1px solid ${theme.colors.white};
    border-radius: ${theme.borderRadius.xxxlarge};
    color: ${theme.colors.white};
    min-width: 8.6rem;
    padding: 1.1rem 2.4rem;
  `,
  tertiaryRacing: ({ theme }) => css`
    background-color: ${theme.colors.brand.tertiary};
    border: 1px solid ${theme.colors.brand.tertiary};
    color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  tertiary: ({ theme }) => css`
    background-color: ${theme.components.buttons.tertiary.background};
    border: 1px solid ${theme.components.buttons.tertiary.border};
    color: ${theme.components.buttons.tertiary.color};
  `,
  tertiaryRound: ({ theme }) => css`
    background-color: ${theme.components.buttons.tertiary.background};
    border: 1px solid ${theme.components.buttons.tertiary.border};
    border-radius: ${theme.borderRadius.xxxlarge};
    color: ${theme.components.buttons.tertiary.color};
  `,
  transparent: ({ theme }) => css`
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: ${theme.borderRadius.xxxlarge};
    color: ${theme.colors.white};
  `,
  selectedRound: ({ theme }) => css`
    background-color: ${theme.components.sports.selections.selected.background};
    border: 1px solid ${theme.components.sports.selections.selected.background};
    border-radius: ${theme.borderRadius.xxxlarge};
    color: ${theme.components.sports.selections.selected.color};
  `,
  white: ({ theme }) => css`
    background-color: ${theme.colors.white};
    color: ${theme.colors.brand.primary};
  `,
  whiteOnBlue: ({ theme }) => css`
    background-color: ${theme.colors.brand.tertiary};
    border: none;
    color: ${theme.colors.white};
  `,
  alert: ({ theme }) => css`
    background-color: ${theme.colors.status.error};
    border: 1px solid ${theme.colors.status.error};
    color: ${theme.colors.white};
  `,
  alertRound: ({ theme }) => css`
    background-color: ${theme.colors.status.error};
    border: 1px solid ${theme.colors.status.error};
    color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  alertRoundOutline: ({ theme }) => css`
    background-color: transparent;
    border: 1px solid ${theme.colors.status.error};
    color: ${theme.colors.status.error};
    border-radius: ${theme.borderRadius.xxxlarge};
  `,
  headerTab: () => css`
    background-color: transparent;
    border-color: transparent;
    font-weight: normal;
  `,
  betslipKeypad: ({ theme }) => css`
    color: ${theme.colors.buttons.primaryContrast};
    background-color: ${theme.colors.buttons.primary};
    border: 1px solid ${theme.colors.buttons.primary};
    border-radius: 10rem;
  `,
  sportsActive: ({ theme }) => css`
    background-color: ${theme.components.sports.selections.color};
    border: 1px solid ${theme.components.sports.selections.borderColor};
  `,
  sportsSelected: ({ theme }) => css`
    background-color: ${theme.components.sports.selections.selected.background};
    border: 1px solid ${theme.components.sports.selections.selected.background};
    color: ${theme.components.sports.selections.selected.color};
  `,
  sportsSuspended: ({ theme }) => css`
    background-color: ${theme.components.sports.selections.color};
    border: 1px solid ${theme.components.sports.selections.color};
    cursor: not-allowed;
    opacity: 0.6;
    font-weight: normal;
  `,
  sportsUnavailable: ({ theme }) => css`
    background-color: ${theme.colors.grey200};
    border: 1px solid ${theme.colors.grey200};
    color: ${theme.colors.status.info};
    cursor: not-allowed;
  `,
  sportsPriceIncrease: ({ theme }) => css`
    background-color: ${theme.colors.positive};
    border: 1px solid ${theme.colors.positive};
    :before {
      content: '▲';
      color: ${theme.colors.status.success};
      position: absolute;
      font-size: 0.8rem;
      bottom: 0;
      right: 0.5rem;
    }
  `,
  sportsPriceDecrease: ({ theme }) => css`
    background-color: ${theme.colors.negative};
    border: 1px solid ${theme.colors.negative};
    :before {
      content: '▼';
      color: ${theme.colors.status.error};
      position: absolute;
      font-size: 0.8rem;
      bottom: 0;
      right: 0.5rem;
    }
  `,
  topScreen: ({ theme }) => css`
    background-color: ${theme.components.sports.selections.color};
    border: none;
  `
};

const ButtonComponent: any = styled.button<{ buttonSize: string; width?: any }>`
  align-items: center;
  border-radius: 0.4rem;
  cursor: pointer;
  display: flex;
  font-size: 1.6rem;
  font-weight: 600;
  justify-content: center;
  opacity: 1;
  position: relative;
  padding: 1.4rem 2.4rem;
  &::-moz-focus-inner,
  &:focus {
    border: none;
  }
  ${({ width }) =>
    width &&
    css`
      width: ${width};
    `};
  ${({ buttonSize }) =>
    buttonSize === 'compact' &&
    css`
      font-size: ${robotoFonts.md};
      padding: 1.2rem 2.4rem;
      min-width: 5rem;
      line-height: 1.6rem;
    `};
  ${({ buttonSize }) =>
    buttonSize === 'standard' &&
    css`
      min-width: 12.8rem;
    `};
  ${({ buttonSize }) =>
    buttonSize === 'fit' &&
    css`
      min-width: 2rem;
      width: 100%;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      flex-basis: auto;
      flex-shrink: 1;
      flex-grow: 1;
    `};
  ${({ buttonSize }) =>
    buttonSize === 'stretch' &&
    css`
      flex-grow: 1;
      width: auto;
    `};
  &:disabled {
    border-color: ${({ theme }) => theme.colors.supporting.accent200};
    cursor: not-allowed;
    opacity: 0.4;
  }
  ${applyStyleModifiers(MODIFIER_CONFIG)};
`;

export const PlayableBalanceButton = styled(Button)<{ color?: string }>`
  ${({ theme, color, disabled }) =>
    !disabled &&
    css`
      border-color: ${color || theme.colors.white};
      color: ${color || theme.colors.white};
    `}
`;
